/**
 * TemplateIndex — the shared, PRESENTATIONAL template-browsing surface extracted
 * from TemplatesPanel so the customer "Templates" tab renders the exact same dense
 * index UI (the "editor-grade" browsing experience) without duplicating markup.
 *
 * Two views, one look:
 *  - `TemplateTable`     — the dense `.tpl-table` spreadsheet list (Template · Model ·
 *                          Status · Actions).
 *  - `TemplateGridCards` — the `.templates__grid` card/tile grid.
 *
 * Both are pure presentation: they own NO data-fetching, NO feature logic. Each page
 * injects its own per-row buttons through the `actions` render-prop (and, for the
 * customer page, a leading bulk-select checkbox through the `leading` slot). This is
 * the hard boundary that keeps admin-only affordances (Delete / Approve / Edit masks /
 * New template) structurally OUT of the customer surface: the customer page simply
 * never passes them into `actions`, so they cannot leak.
 *
 * Every string a row prints comes from `templateLabels` — the one formatter layer shared
 * with the customer page, the preview drawer and the catalog tree, so the status pill,
 * the Model label, the category and the version can no longer drift between surfaces.
 */
import { useHidesGuide } from "../generate/whiteGuide.js";
import { useState, type ReactNode } from "react";
import { DataTable, type Column } from "../ui/DataTable.js";
import type { ProductTemplate } from "@genz/platform";
import { bakedPreviewUrl, type BakedAssetRefs } from "./bakedSync.js";
import { presenceBadge, marksRowOffDevice, type DevicePresence } from "./devicePresence.js";
import { isMockupOnlyTemplate } from "./wireMasks.js";
import {
  brandOf, headStatusOf, headSubline, modelLabel, statusBadgeClass, statusLabel, styleLabel,
  templateSubline, versionLabel,
} from "./templateLabels.js";
import "./customer-templates.css";

/** A template's best-available preview image URL: the baked mockup preview, then the
 *  server-resolved row thumbnail (`previewRef`, available before sync), then the first view's
 *  base-case image. Undefined → no preview yet (renders the soft phone placeholder). Shared by
 *  the list thumbnail here and CustomerTemplates' preview drawer so the chain never drifts. */
export function templatePreviewUrl(t: ProductTemplate): string | undefined {
  return keyedPreviewUrl(t) ?? t.previewRef ?? t.currentVersion?.payload?.views?.[0]?.base_case_image_ref ?? undefined;
}

/** A4 — the preview a customer may see: the keyed mock-up only, never the raw (magenta) case
 *  photo. The server's `previewRef` is the baked image exactly when the version has one. */
export function keyedPreviewUrl(t: ProductTemplate): string | undefined {
  return bakedPreviewUrl(t.currentVersion?.bakedAssets as BakedAssetRefs | null | undefined)
    ?? (t.currentVersion?.hasBakedAssets ? t.previewRef ?? undefined : undefined);
}

/** Whether the viewer may see only keyed previews: every signed-in role below super-admin. */
export const useKeyedPreviewsOnly = useHidesGuide;

/** The rendered box of each thumbnail size, in CSS pixels — mirrors `.ctpl-thumb--sm` /
 *  `--lg` in `customer-templates.css`. Stamped onto the `<img>` as `width`/`height` so the
 *  browser reserves the space BEFORE the image decodes (WS-B B-10 / F-27): an `<img>` with no
 *  intrinsic size occupies zero until it loads, so every row jumped as the previews landed —
 *  half of what "thumbnails vary" was actually describing. */
const THUMB_BOX: Record<"sm" | "lg" | "block", { w: number; h: number }> = {
  /* WS-Z1 / Z0 rule 6: 24×30 is THE row thumbnail, declared once as `.table-thumb` in
     theme.css so page waves stop inventing a size each. It was 34×46 here, which set the
     dense row's height by itself — the thing the rule exists to stop. Still 3:4. */
  sm: { w: 24, h: 30 },
  lg: { w: 132, h: 176 },
  /* WS-K3: the full-width detail block's thumbnail. 72×96 is 3:4 — the case aspect — and it is
     the shape contract AUD-1 F-27 observed the thumbnails never had: `sm` is 24×30 (also 3:4 —
     it was 34×46 until WS-Z1 made the row thumb the shared `.table-thumb`) and `lg` is 132×176
     (3:4), so all three are now one ratio at three sizes rather than three independent boxes. */
  block: { w: 72, h: 96 },
};

/** The template list/card thumbnail — the baked preview image when one exists, else the soft
 *  phone-chip placeholder (the CSS `::after`). Falls back to the placeholder if the image 404s. */
function TemplateThumb({ t, size }: { t: ProductTemplate; size: "sm" | "lg" | "block" }) {
  // Key the failure to the specific URL (not a latched boolean) so a template that later gains a
  // valid preview — e.g. an asset that finished uploading after a first 404 — shows it instead of
  // being stuck on the placeholder for the life of the row.
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
  const src = useKeyedPreviewsOnly() ? keyedPreviewUrl(t) : templatePreviewUrl(t);
  const failed = src !== undefined && failedSrc === src;
  const showing = Boolean(src) && !failed;
  const box = THUMB_BOX[size];
  return (
    <span
      /* Z0 rule 6: the ROW thumb is the shared `.table-thumb` — the size and the frame come
         from theme.css. `.ctpl-thumb--sm` keeps only what is this surface's own: the soft
         phone-chip placeholder it draws when there is no preview yet. */
      className={`ctpl-thumb ctpl-thumb--${size}${size === "sm" ? " table-thumb" : ""}`}
      aria-hidden="true"
      // The placeholder was a generic phone glyph with nothing to say whether the preview was
      // loading, broken, or simply never baked. Naming the state costs one attribute.
      title={showing ? undefined : "No preview yet"}
    >
      {showing && (
        <img
          className="ctpl-thumb__img"
          src={src}
          alt=""
          loading="lazy"
          width={box.w}
          height={box.h}
          onError={() => setFailedSrc(src)}
        />
      )}
    </span>
  );
}

/** Back-compat alias for the shared badge-class resolver (call sites import `statusBadge`
 *  from here; the rule itself lives in `templateLabels`). */
export const statusBadge = statusBadgeClass;
export { statusLabel };

/** The default Model-column value — the deduped display label, never the `phoneModelId`
 *  (a raw UUID leaked here whenever the server couldn't resolve the phone model). */
function defaultModel(t: ProductTemplate): string {
  return modelLabel({ model: t.display?.model });
}

/** The default Brand-column value (D-3 iii). `brandOf` never guesses by splitting the model
 *  label — a wrong Brand column is worse than an empty one — so this is an em dash whenever
 *  neither the server nor the caller's phone-model catalog can answer. */
function defaultBrand(t: ProductTemplate): ReactNode {
  return brandOf(t);
}

/** Visually-hidden header text for the two icon-only columns (F-22).
 *  WS-K2 should promote `.ctpl-sr-only` to a global utility when it builds `DataTable`. */
/* WS-K2 promoted this idiom to the global `.sr-only` utility (K2-8). `DataTable` renders
   it through `srOnlyHeader`, and `.ctpl-sr-only` survives in the CSS as an alias so no
   selector moves. */

/**
 * The device-state marker — D-28: such a row is NEVER dropped, it is SHOWN with a state, on
 * both lists.
 *
 * WS-P: it used to be one hard-coded "Not on this device" driven by the localStorage hide-list,
 * so a template never synced here and one whose cloud version has no bake at all both rendered
 * as present. Four causes, one silence. The sentence now comes from `presenceBadge`, and
 * `data-presence` carries the precise value for anyone who needs to branch on it.
 *
 * `data-testid="template-off-device"` is kept for the three states that used to produce it
 * (WS-D2's layout probe and existing tests depend on it); `not-published` is new and does not
 * claim the template is off-device, because it is sitting right here.
 */
function PresenceBadge({ presence, quiet }: { presence: DevicePresence; quiet?: boolean }) {
  const badge = presenceBadge(presence);
  if (!badge) return null;
  return (
    <span
      className={badge.className}
      title={badge.title}
      data-presence={presence}
      data-testid={marksRowOffDevice(presence) ? "template-off-device" : "template-presence"}
      // review-1 m-5: the marker STAYS in the DOM — `data-presence`, the legacy
      // `template-off-device` testid and the row's `data-on-device="false"` are contracts
      // (WS-D2's layout probe, WS-K2's migration, the pre-WS-P tests). Only the pixels go.
      hidden={quiet || undefined}
    >
      {badge.label}
    </span>
  );
}

/**
 * Which presences render as a VISIBLE badge on this list (review-1 m-5, concern 2).
 *
 * On the cloud-browse list most rows are legitimately not synced here, so `Not synced here`
 * appeared on nearly every row — immediately beside a button that already reads **Sync**. The
 * information was correct and redundant, and the real cost was dilution: four badges where
 * three rows in four carry one is not a signal, and the two `badge--warn` states this wave
 * added (`Mock-up not downloaded`, `Mock-up not published`) are exactly what had to stand out.
 *
 * So only `not-synced` is quieted, only where a Sync button carries the same sentence. On the
 * admin list there is no such button and the badge is the only statement, so it stays visible.
 * The DOM marker is unchanged in both.
 */
function quietPresence(presence: DevicePresence, quietNotSynced?: boolean): boolean {
  return !!quietNotSynced && presence === "not-synced";
}

/**
 * The template NAME — a real button when there is somewhere to go, a plain span when there
 * is not (F-23).
 *
 * Both index components rendered an always-present `<button>` whose `onClick` was
 * `onOpen ? … : undefined`, so a page that passes no `onOpen` shipped a focusable,
 * pointer-cursored control that did nothing when activated — the most direct kind of broken
 * promise a UI can make. Same `data-testid` either way so nothing else moves.
 */
function OpenControl({
  t, onOpen, testId, title, gateOpen,
}: {
  t: ProductTemplate;
  onOpen?: (t: ProductTemplate) => void;
  testId: string;
  title: string;
  /** F-8: the testid of the dialog this control opens, when it opens one. See `openGateOpen`. */
  gateOpen?: string;
}) {
  /* review-1 I-2: the one-line ellipsis lives on this INNER span, never on the control.
     `overflow: hidden` on the control clipped its own 32px `::before` hit ring back to the
     20px text box, so the WCAG 2.5.8 target G-26 reported was not hit-testable — 3px above
     the name landed on the cell wrapper and nothing happened. The name still ellipsises;
     the ring is now painted by a box that does not clip. */
  const label = <span className="tpl-table__open-text">{t.name}</span>;
  if (!onOpen) {
    return <span className="tpl-table__open tpl-table__open--static" data-testid={testId}>{label}</span>;
  }
  return (
    <button
      type="button"
      className="tpl-table__open"
      onClick={() => onOpen(t)}
      data-testid={testId}
      data-gate-open={gateOpen}
      title={title}
    >
      {label}
    </button>
  );
}

/** The Model cell's tooltip — only when the value really is text. */
function modelTitle(value: ReactNode): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** The default name sub-line: the real version, then the shared template facts
 *  ("Version 3 · IM-16P-CLR · Phone case (TPU) · Clear TPU · PNG · 2 views"). */
function defaultSubline(t: ProductTemplate): ReactNode {
  return `${versionLabel(t.currentVersion?.versionNumber)} · ${templateSubline(t)}`;
}

/** Props shared by the table + grid presentations. */
interface TemplateIndexProps {
  rows: ProductTemplate[];
  /** Per-row action buttons — the page's ONLY injection point for behavior. */
  actions: (t: ProductTemplate) => ReactNode;
  /** Click the template name → open (editor on the admin page, preview on the customer page). */
  onOpen?: (t: ProductTemplate) => void;
  /** Optional leading cell before the Template column (e.g. a bulk-select checkbox). */
  leading?: (t: ProductTemplate) => ReactNode;
  /** Override the name sub-line (defaults to "Version N · code · style"). */
  subline?: (t: ProductTemplate) => ReactNode;
  /** Override the Model-column value (defaults to the resolved display model). */
  model?: (t: ProductTemplate) => ReactNode;
  /** Override the Brand-column value (defaults to `brandOf`, which the page overrides with
   *  the phone-model catalog it has already loaded as the pre-WS-C fallback). */
  brand?: (t: ProductTemplate) => ReactNode;
  /** D-28: is this template present on THIS device? `false` marks the row
   *  (`data-on-device="false"` + a "Not on this device" badge) — it never removes it.
   *
   *  WS-P: superseded by `presence` below, and kept only as the fallback for callers that have
   *  not been migrated. When BOTH are given, `presence` wins — it can say which of the four
   *  reasons applies, and this boolean cannot. */
  onDevice?: (t: ProductTemplate) => boolean;
  /** WS-P / D-28: the precise device state for this row (see `devicePresence.ts`). Supersedes
   *  `onDevice`: one flag could not distinguish "you removed it" from "never synced here" from
   *  "the mock-up was never published", and rendered all three as present. */
  presence?: (t: ProductTemplate) => DevicePresence;
  /** review-1 m-5: render the `not-synced` state as a DOM marker only, with no visible badge —
   *  for lists whose own row button already says "Sync". Every other presence is unaffected,
   *  and the marker (`data-presence`, `template-off-device`, `data-on-device`) is unchanged. */
  quietNotSynced?: boolean;
  /** Flags a row as queued/selected → `data-queued`. */
  queued?: (t: ProductTemplate) => boolean;
  /** Title/tooltip on the name button. */
  openTitle?: string;
  /** Extra class on the list container (list `<table>` / card `<ul>`). */
  className?: string;
  /** Testid for the list container (default "templates-list"). */
  listTestId?: string;
  /** Testid for each row/card (default "template-item"). */
  itemTestId?: string;
  /** Testid for the name button (default "template-open"). */
  openTestId?: string;
  /**
   * F-8 — the testid of the DIALOG the name button opens, when it opens one.
   *
   * The same control routes to the Editor on the authoring page and opens the preview drawer
   * on the customer page (`TemplateListPage.tsx`'s `openRow`), so only the caller knows which.
   * Rendered as `data-gate-open`, which is the ONLY seam `design-gate.mjs`'s G-26o may use to
   * open a dialog: a gate that guessed which control opens something would drive the product
   * instead of measuring it. Omitted on the authoring page, where the control navigates.
   */
  openGateOpen?: string;
}

/** A "Make template" mock-up: no print area, so the Mock-ups batch does not offer it. */
function MockupOnlyBadge() {
  return (
    <span className="badge badge--neutral" title="No print area — not offered in Mock-ups" data-testid="template-mockup-only">
      Mock-up only
    </span>
  );
}

/** The dense spreadsheet list — Template · Model · Status · Actions. */
export function TemplateTable({
  rows,
  actions,
  onOpen,
  leading,
  subline = defaultSubline,
  model = defaultModel,
  brand = defaultBrand,
  onDevice,
  presence,
  quietNotSynced,
  queued,
  openTitle = "Open to edit / re-mask",
  className = "templates__list",
  listTestId = "templates-list",
  itemTestId = "template-item",
  openTestId = "template-open",
  openGateOpen,
}: TemplateIndexProps) {
  /* WS-P: `presence` wins when given — it can name WHICH of the four causes applies, where the
     legacy boolean could only say "not here". Derived once per row, and read by both the name
     cell's badge and the row's `data-on-device` marker so the two cannot disagree. */
  const presenceOf = (row: ProductTemplate): DevicePresence =>
    presence?.(row) ?? ((onDevice?.(row) ?? true) ? "ready" : "removed");

  const columns: Column<ProductTemplate>[] = [
    ...(leading
      ? [{
          key: "lead",
          header: "Select",
          srOnlyHeader: true,
          headerClassName: "tpl-table__lead-col",
          className: "tpl-table__lead",
          render: leading,
        } as Column<ProductTemplate>]
      : []),
    {
      key: "template",
      header: "Template",
      className: "tpl-table__name",
      render: (row) => {
        const state = presenceOf(row);
        return (
          <div className="tpl-table__name-cell">
            <TemplateThumb t={row} size="sm" />
            <div className="tpl-table__name-text">
              <OpenControl t={row} onOpen={onOpen} testId={openTestId} title={openTitle} gateOpen={openGateOpen} />
              <span className="tpl-table__sub">{subline(row)}</span>
              {isMockupOnlyTemplate(row) && <span className="tpl-table__sub"><MockupOnlyBadge /></span>}
              {/* WS-P: a SIBLING of the sub-line, not inside it. The old badge lived in that
                  span, so the sub-line's text run was "Version 3 · … · 1 view Not on this
                  device" — a badge pretending to be prose, and a sub-line nothing could assert
                  on. Now that four states can badge a row instead of one, keeping it inside
                  would make that permanent. */}
              {state !== "ready" && (
                <span className="tpl-table__sub tpl-table__presence">
                  <PresenceBadge presence={state} quiet={quietPresence(state, quietNotSynced)} />
                </span>
              )}
              {/* The head version's own line -- "v2 draft pending" / "v3 awaiting approval" --
                  only when the head is AHEAD of the active version. */}
              {headSubline(row) && (
                <span className="tpl-table__sub tpl-table__sub--head">{headSubline(row)}</span>
              )}
            </div>
          </div>
        );
      },
    },
    { key: "brand", header: "Brand", className: "tpl-table__brand", render: (row) => brand(row) },
    {
      key: "model",
      header: "Model",
      className: "tpl-table__model",
      /* `title` = the full label, ON THE CELL. Model is the one column whose entire job is
         to say WHICH model, and "Apple iPhone 16" / "... 16 Pro" / "... 16 Pro Max" differ
         only in the suffix, so the value must stay recoverable when the column is squeezed —
         and a browser shows a title only for the element it sits on, so a span inside the
         cell would not do. (Both call sites return a string; anything else gets no tooltip
         rather than "[object Object]".) */
      cellProps: (row) => ({ title: modelTitle(model(row)) }),
      render: (row) => model(row),
    },
    {
      key: "status",
      header: "Status",
      /* WS-P: named, because the STATUS pill is no longer the only badge in a row — a presence
         badge now sits in the name cell, and `tbody .badge` used to be how callers reached for
         this one. */
      className: "tpl-table__status",
      render: (row) => {
        // The HEAD version's status, not the active one: a template sitting on an active v1
        // with a v2 Draft above it belongs under the Draft chip and must say so here.
        const status = headStatusOf(row);
        return status ? <span className={`badge ${statusBadge(status)}`}>{statusLabel(status)}</span> : null;
      },
    },
    {
      key: "actions",
      header: "Actions",
      srOnlyHeader: true,
      headerClassName: "tpl-table__actions-col",
      className: "tpl-table__actions",
      render: actions,
    },
  ];

  /* Migrated behind an UNCHANGED contract (K2-8): `.tpl-table` and the caller's class, the
     `.table-scroll` wrapper, `data-view="list"`, the list testid, the per-row `data-queued` /
     `data-on-device`, `scope="col"` on every header and the two visually-hidden column names
     all survive. WS-B's four suites are the proof, and they are byte-unchanged.

     Still deliberately NOT `role="grid"`: DataTable implements no roving focus and no
     arrow-key cell navigation, so announcing a grid would be a lie. WS-B's reasoning
     survives the migration verbatim. */
  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.templateId}
      className={`tpl-table ${className}`.trim()}
      testId={listTestId}
      head={{ "data-view": "list" }}
      /* WS-B's `template-index.test.tsx` selects `thead .ctpl-sr-only` (F-22). A test
         asserting a class is a contract, so the legacy class stays in the DOM and the global
         `.sr-only` utility is added beside it — nothing is renamed. */
      srOnlyClassName="sr-only ctpl-sr-only"
      rowProps={(row) => ({
        "data-testid": itemTestId,
        "data-queued": queued?.(row) ? "true" : undefined,
        /* WS-P: from the four-state presence, not the legacy boolean — `marksRowOffDevice`
           is the single place that decides which states mark a row. */
        "data-on-device": marksRowOffDevice(presenceOf(row)) ? "false" : undefined,
      })}
    />
  );
}

/** Props for the card grid — same injection points, plus the two card sub-lines. */
interface TemplateGridProps extends TemplateIndexProps {
  /** The block's IDENTITY line (default "model · style"). The browse page overrides it with
   *  `brand · model · product type`; the default is unchanged because four suites mount this
   *  component without the prop and pin the default string. */
  meta?: (t: ProductTemplate) => ReactNode;
  /** The block's FACTS line (default "Version N · code · category · style · format · N views"). */
  versions?: (t: ProductTemplate) => ReactNode;
  /** WS-K3 K3-8: is this row ticked for a bulk action? Drives `data-checked` and the
   *  `--selected-row` tier — a marker AND a tint, never a tint alone (C7). */
  checked?: (t: ProductTemplate) => boolean;
}

/** The default card meta line: "model · style". It used to print the raw `caseStyleId`
 *  UUID on EVERY card — not as a fallback — which is exactly what the file's own
 *  docstring forbids ("No label → omit, never the id"). */
function defaultMeta(t: ProductTemplate): ReactNode {
  return `${defaultModel(t)} · ${styleLabel(t.display?.style)}`;
}

/** The default card versions line — the same sub-line the table renders. */
function defaultVersions(t: ProductTemplate): ReactNode {
  return defaultSubline(t);
}

/**
 * The FULL-WIDTH DETAIL BLOCK (WS-K3 K3-5, owner item O-3) — same export, same props, same
 * testids, same `data-view="card"`, same `.templates__grid` hook, a completely different shape.
 *
 * ```
 * ┌────────────────────────────────────────────────────────────────────────────┐
 * │ ▢ ┌──────┐  iPhone 16 Pro — Clear TPU                          ● Active     │
 * │   │ 72×96│  Apple · iPhone 16 Pro · Phone case (TPU)                        │
 * │   │ thumb│  Version 3 · IM-16P-CLR · Clear TPU · PNG · 2 views              │
 * │   └──────┘  On this device                                                  │
 * │                                      [Sync]  [Create Mock-up]  [⋯ Remove]   │
 * └────────────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * **Why reshape rather than add a third view.** `templates.test.tsx` pins `data-view="list"` as
 * the admin default and `data-view="card"` + `.templates__grid` after the toggle;
 * `template-list-page.test.tsx` clicks `templates-view-card`; `cross-surface-consistency` and
 * `template-index` mount this component eight times between them. A third segment would mean
 * editing four suites, two of them untouchable. **The card view IS the blocks view.**
 *
 * Four deliberate departures from the old card:
 *
 * 1. **No `.card` wrapper and no shadow.** `docs/design.md`'s *Avoiding AI-slop tells* names the
 *    identical-rounded-card kit with one soft grey shadow, and 24 of them in a column is that
 *    tell exactly. The block is a hairline border on `--surface`.
 * 2. **The name is `OpenControl`, verbatim** — the same control the table row uses, carrying
 *    K2 review-1 I-2's hit-testable 32 px ring. One name control in the product, not two.
 * 3. **The thumbnail is 72×96 and sits OUTSIDE the name control**, so the control's accessible
 *    name is the template's name and nothing else.
 * 4. **Selection is the `--selected-row` tier** (`data-checked`), which K2 defined in
 *    `theme.css` for precisely this: a full-strength fill across a row is a wall of colour and
 *    the row's own text still has to be read.
 */
export function TemplateGridCards({
  rows,
  actions,
  onOpen,
  leading,
  meta = defaultMeta,
  versions = defaultVersions,
  onDevice,
  presence,
  quietNotSynced,
  queued,
  checked,
  openTitle = "Open to edit / re-mask",
  className = "",
  listTestId = "templates-list",
  itemTestId = "template-item",
  openTestId = "template-open",
  openGateOpen,
}: TemplateGridProps) {
  return (
    <ul
      className={`templates__grid ${className}`.trim()}
      data-testid={listTestId}
      data-view="card"
      /* The ONE new hook. `browse.css` scopes every block rule behind it, so
         `.templates__grid` in components.css is NOT rewritten and the Mock-ups/admin card grid
         renders exactly as it does today — an additive attribute-scoped rule that out-specifies
         the base by one attribute, with no `!important` and no edit to a WS-K2 stylesheet. */
      data-browse="blocks"
    >
      {rows.map((t) => {
        const status = headStatusOf(t);
        const head = headSubline(t);
        // WS-P: `presence` wins when given — see the table render site above.
        const state = presence?.(t) ?? (onDevice?.(t) ?? true ? "ready" : "removed");
        const present = !marksRowOffDevice(state);
        return (
          <li
            key={t.templateId}
            className="templates__item"
            data-testid={itemTestId}
            data-queued={(queued?.(t)) || undefined}
            data-on-device={present ? undefined : "false"}
            data-checked={checked?.(t) ? "true" : undefined}
          >
            {leading && <div className="templates__item-lead">{leading(t)}</div>}
            {/* Outside the name control: the control's accessible name is the template's name,
                and the picture is decoration the name already describes. */}
            <TemplateThumb t={t} size="block" />
            <div className="templates__item-body">
              <div className="templates__item-head">
                {/* F-20/F-23 both live in OpenControl: phrasing content only, and a <span>
                    rather than a dead <button> when there is nowhere to go. */}
                <OpenControl t={t} onOpen={onOpen} testId={openTestId} title={openTitle} gateOpen={openGateOpen} />
                {status && <span className={`badge ${statusBadge(status)}`}>{statusLabel(status)}</span>}
                {isMockupOnlyTemplate(t) && <MockupOnlyBadge />}
              </div>
              <p className="muted templates__meta">{meta(t)}</p>
              <p className="faint templates__versions">{versions(t)}</p>
              {head && <p className="faint templates__versions tpl-table__sub--head">{head}</p>}
              {/* D-28: a row this device cannot fully render is MARKED, never dropped — and
                  the good state is a WORD, not the absence of a badge (C7: state is never
                  colour-only, and "no badge" is not a state a reader can perceive). */}
              {/* An EMPTY paragraph is not a state — a quieted `not-synced` badge (review-1 m-5
                  on the browse list, where the row's own button already says Sync) would
                  otherwise leave a blank line in every block. Render the line only when it has
                  something to say; the DOM marker still ships inside PresenceBadge. */}
              {(state === "ready" || !quietPresence(state, quietNotSynced)) && (
                <p className="faint templates__device">
                  {state === "ready"
                    ? "On this device"
                    : <PresenceBadge presence={state} quiet={false} />}
                </p>
              )}
              {quietPresence(state, quietNotSynced) && (
                <PresenceBadge presence={state} quiet />
              )}
            </div>
            <div className="templates__row-actions">{actions(t)}</div>
          </li>
        );
      })}
    </ul>
  );
}
